import type { LetterStatus, MotusOutcome } from './state.js';

/** Namespace socket de MOTUS. */
export const NS_MOTUS = '/motus';

/** Events entrants (client → serveur). Acteur & tour vérifiés serveur. */
export const MotusClientEvents = {
  SetWord: 'motus:set_word',
  Typing: 'motus:typing',
  Guess: 'motus:guess',
  Abandon: 'motus:abandon',
  /** Demande/accepte une revanche (rôles inversés, spec §5.5). */
  Rematch: 'motus:rematch',
} as const;

/** Events sortants (serveur → client). Aucun ne porte le secret (sauf answer si finished). */
export const MotusServerEvents = {
  State: 'motus:state',
  RoundStarted: 'motus:round_started',
  OpponentTyping: 'motus:opponent_typing',
  GuessResult: 'motus:guess_result',
  RoundFinished: 'motus:round_finished',
  /** Rappel du mot secret — émis AU CHOOSER SEUL (jamais broadcast/guesser), Fix « voir son mot ». */
  YourSecret: 'motus:your_secret',
  /** Un joueur a proposé une revanche (en attente de l'autre). */
  RematchOffered: 'motus:rematch_offered',
  /** Les deux ont accepté : nouvelle partie prête (grant par joueur). */
  RematchReady: 'motus:rematch_ready',
  /** Timeout de tour (F5, spec §10) : essai consommé sans proposition. */
  TurnTimeout: 'motus:turn_timeout',
  /** Un host/player n'a plus de connexion — grâce en cours (F5). */
  OpponentDisconnected: 'motus:opponent_disconnected',
  /** Le joueur est revenu pendant la grâce (F5). */
  Reconnected: 'motus:reconnected',
  Error: 'motus:error',
} as const;

export interface RoundStartedPayload {
  length: number;
  firstLetter: string;
  maxAttempts: number;
  guesserUserId: string;
  chooserUserId: string;
  turnTimeoutSec: number;
}

export interface GuessResultPayload {
  attemptIndex: number;
  /** Lettres tapées par le devineur (publiques), pas le secret. */
  letters: string[];
  statuses: LetterStatus[];
  attemptsUsed: number;
  attemptsLeft: number;
  solved: boolean;
}

export interface RoundFinishedPayload {
  outcome: MotusOutcome;
  solved: boolean;
  winnerUserId?: string;
  attemptsUsed: number;
  /** Le mot — présent UNIQUEMENT à la fin de manche (ADR 0003 §9.3). */
  answer?: string;
}

/** Timeout de tour (F5) : l'essai `attemptIndex` est consommé (ABSENT*, mot vide). */
export interface TurnTimeoutPayload {
  attemptIndex: number;
  attemptsUsed: number;
  attemptsLeft: number;
}

/** Grâce de déconnexion (F5) : au-delà de graceMs sans retour → abandon. */
export interface OpponentDisconnectedPayload {
  userId: string;
  graceMs: number;
}

export interface ReconnectedPayload {
  userId: string;
}

export interface OpponentTypingPayload {
  userId: string;
  draft: string;
}

/** Rappel du mot secret pour son auteur (le chooser). Émis à `user:<chooserId>` uniquement. */
export interface YourSecretPayload {
  secret: string;
}

/** Un joueur a proposé une revanche ; l'autre doit accepter pour lancer. */
export interface RematchOfferedPayload {
  byUserId: string;
}

/**
 * Revanche acceptée par les deux : une NOUVELLE partie (rôles inversés) est prête.
 * Émis PAR JOUEUR (room `user:<id>`) : chacun reçoit son propre `joinGrant` et son rôle.
 */
export interface RematchReadyPayload {
  matchId: string;
  joinGrant: string;
  role: 'chooser' | 'guesser';
}

export interface MotusErrorPayload {
  code:
    | 'INVALID_WORD'
    | 'WORD_IS_CONJUGATED_VERB'
    | 'INVALID_GUESS'
    | 'NOT_YOUR_TURN'
    | 'ROUND_CLOSED'
    | 'NOT_CHOOSER'
    | 'NOT_GUESSER'
    | 'BAD_STATE';
  message: string;
}
